/**
 * The vertical Hips handle (ANIMATE mode, whenever the hips-height channel is live on the rig).
 *
 * A crouch is the one thing an animator cannot author with rotations alone: bend the knees and
 * the pelvis stays at rest height while the feet sink through the floor. `hipsY` is that missing
 * channel, and this is its viewport affordance — a small dot on the Hips joint that drags along
 * the model's up vector and nowhere else.
 *
 * It is modelled on `ikHandles.ts` (visible dot + invisible pick mesh, picked on pointer-up
 * before the skeleton, glued to the joint every frame while inactive, one shared
 * `TransformControls` taken through a claim), with three differences that matter:
 *
 *  - the claim restricts the gizmo to its Y axis (`GizmoClaim.axes`). X/Z would author horizontal
 *    travel, which the clip format cannot hold and the product rule forbids: "Root belongs to the
 *    game, Hips belongs to the clip";
 *  - **the handle does not sit ON the Hips joint.** It is pushed `HANDLE_OFFSET_LEGS` of a leg
 *    length along the model's FORWARD vector and its pick sphere is smaller than the skeleton
 *    overlay's (0.035 vs `skeletonLayer`'s 0.055). Concentric spheres tested in mount's pick order
 *    (handle first) made the Hips joint unpickable: every click on the pelvis grabbed the height
 *    handle and the pelvis could no longer be selected for rotation at all. Forward — rather than
 *    up or sideways — keeps it clear of the Hips joint, of both UpperLeg joints and of the Spine,
 *    and puts it where a camera looking at the character's front can always reach it;
 *  - the claim is RELEASED whenever the selection is no longer the Hips, whenever ANIMATE is left
 *    and whenever the rig is re-bound — checked every frame in `update()`, not only on a viewport
 *    pick, because the hierarchy panel and the dope sheet select bones too. Without that, one grab
 *    left the Y arrow outranking the bone gizmo forever: the inspector showed the newly selected
 *    arm while the viewport still showed the hips handle and `R` did nothing. The IK layer keeps
 *    its claim until IK is switched off, because its handles ARE the mode; this one is a second
 *    control on a bone the user still rotates.
 *
 * Lowering the hips deliberately does NOT re-solve the legs. IK here is momentary and only runs
 * while a handle is dragged (`actions/ik.ts`), so a hips drop moves the pelvis and leaves the leg
 * rotations exactly as authored — the animator crouches by keying the knees, the same way they
 * would in any other tool.
 */

import {
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Quaternion,
  Raycaster,
  SphereGeometry,
  Vector2,
  Vector3,
} from 'three';
import type { Camera } from 'three';

import type { QuaternionTuple, Vec3Tuple } from '../../shared';
import type { EditorContext } from '../context';
import type { GizmoClaim } from './gizmos';

const HANDLE_RADIUS = 0.03;
/**
 * Deliberately SMALLER than `skeletonLayer`'s 0.055 joint pick sphere. This layer is tested first
 * in mount's pointer order, so anything it can swallow, it does swallow.
 */
const PICK_RADIUS = 0.035;
/** How far in front of the pelvis the handle floats, in leg lengths. */
const HANDLE_OFFSET_LEGS = 0.15;

/**
 * The claim key. It doubles as the coalesce key of the drag (`beginHipsDrag`), so a whole gesture
 * is one undo entry — the same arrangement `pose:${bone}` has for a rotation drag.
 */
export const HIPS_HANDLE_KEY = 'hips-y';

/*
 * A FUNCTION code like the IK green/violet, not an accent: this handle moves height, so it is
 * pulled onto the palette's blue (`--blue` #4772b3) and stays clear of the orange selection
 * colour and of the IK pair. The brighter tint marks "the gizmo is on me".
 */
const COLOR_HIPS = new Color(0x4772b3);
const COLOR_HIPS_ACTIVE = new Color(0x8fb2e6);

/** Local +Y of a handle oriented by `orientationForUp`. */
const LOCAL_UP = new Vector3(0, 1, 0);

/**
 * The model's FORWARD (+Z canonical) in world space, from the binding's world correction — the
 * same derivation `BoundRig.upWorld()` uses for +Y, because the handle's offset has to follow the
 * character, not the scene. A missing or degenerate correction falls back to world +Z.
 */
export function forwardFromWorldCorrection(
  worldCorrection: QuaternionTuple | undefined | null,
): Vec3Tuple {
  const w = worldCorrection;
  if (!w || w.some((n) => !Number.isFinite(n))) return [0, 0, 1];
  const q = new Quaternion(w[0], w[1], w[2], w[3]);
  if (q.lengthSq() <= 1e-12) return [0, 0, 1];
  const forward = new Vector3(0, 0, 1).applyQuaternion(q.normalize().invert());
  if (forward.lengthSq() <= 1e-12) return [0, 0, 1];
  forward.normalize();
  return [forward.x, forward.y, forward.z];
}

/**
 * The handle rotation that makes its local +Y point along the model's up vector.
 *
 * The claim is `space: 'local'`, so the gizmo's Y arrow IS the object's local +Y: orienting the
 * object is what aims the only enabled axis at "up" for this model. A degenerate or non-finite up
 * vector falls back to identity rather than to a NaN quaternion that would take the whole scene
 * graph with it.
 */
export function orientationForUp(up: Vec3Tuple, out: Quaternion = new Quaternion()): Quaternion {
  const v = new Vector3(up[0], up[1], up[2]);
  if (!Number.isFinite(v.x) || !Number.isFinite(v.y) || !Number.isFinite(v.z)) return out.identity();
  if (v.lengthSq() <= 1e-12) return out.identity();
  return out.setFromUnitVectors(LOCAL_UP, v.normalize());
}

/**
 * The `hipsY` a drag has produced: how far the handle travelled ALONG the up vector, in leg
 * lengths, added to where the drag started.
 *
 * Only the component along `up` counts — the gizmo's ray-plane translation can drift sideways by
 * a pixel or two near a grazing camera angle, and the channel has no sideways to store. A rig
 * with no usable leg length (`legLength <= 0`, i.e. the channel is off) returns the start value:
 * there is no scale to convert world units into, and inventing one would move the pelvis by an
 * arbitrary amount.
 */
export function hipsYFromDrag(
  startHipsY: number,
  startWorld: Vec3Tuple,
  currentWorld: Vec3Tuple,
  up: Vec3Tuple,
  legLength: number,
): number {
  if (!(legLength > 0)) return startHipsY;
  const ux = up[0];
  const uy = up[1];
  const uz = up[2];
  const length = Math.hypot(ux, uy, uz);
  if (!(length > 0)) return startHipsY;

  const dx = currentWorld[0] - startWorld[0];
  const dy = currentWorld[1] - startWorld[1];
  const dz = currentWorld[2] - startWorld[2];
  const along = (dx * ux + dy * uy + dz * uz) / length;
  const next = startHipsY + along / legLength;
  return Number.isFinite(next) ? next : startHipsY;
}

/** What a headless driver can ask about the handle (`testSurface.hipsHandle()`). */
export interface HipsHandleProbe {
  worldPosition: Vec3Tuple;
  /** True while the handle owns the shared gizmo. */
  active: boolean;
  visible: boolean;
}

/** The viewport handle's read-only window onto this layer, for the test surface. */
export interface ViewportHipsProbe {
  hipsHandle(): HipsHandleProbe | null;
}

export interface HipsHandleLayer {
  /**
   * Reposition the handle from the rig and drop the claim when it is no longer ours. Skips the
   * re-glue while the handle itself is dragged.
   */
  update(draggingKey: string | null): void;
  /** Gizmo claim while the handle is grabbed, or null. */
  claim(): GizmoClaim | null;
  /** Pointer pick; selects `Hips` and takes the claim. True when the handle was hit. */
  pick(ndc: Vector2, camera: Camera): boolean;
  /** Hand the gizmo back (another pick won). The handle stays visible and re-glues. */
  release(): void;
  /** Read-only state for the headless surface; null when the channel is not live. */
  probe(): HipsHandleProbe | null;
  dispose(): void;
}

export function createHipsHandleLayer(
  overlayGroup: Group,
  ctx: EditorContext,
): HipsHandleLayer {
  const group = new Group();
  group.name = 'asx-hips-y';
  group.userData.editorOnly = true;
  overlayGroup.add(group);

  const dotGeometry = new SphereGeometry(HANDLE_RADIUS, 12, 10);
  const pickGeometry = new SphereGeometry(PICK_RADIUS, 6, 5);
  const material = new MeshBasicMaterial({ color: COLOR_HIPS.clone(), depthTest: false });
  const pickMaterial = new MeshBasicMaterial({ visible: false, depthTest: false });

  const handle = new Object3D();
  handle.name = 'asx-hips-y-handle';
  const dot = new Mesh(dotGeometry, material);
  dot.renderOrder = 1000;
  dot.frustumCulled = false;
  handle.add(dot);
  const pickMesh = new Mesh(pickGeometry, pickMaterial);
  pickMesh.frustumCulled = false;
  handle.add(pickMesh);
  group.add(handle);

  /** True between a successful `pick()` and a `release()` — i.e. while the gizmo is ours. */
  let active = false;

  /** Drag origin, so the gesture is one continuous mapping instead of per-step deltas. */
  let startHipsY = 0;
  const startWorld: Vec3Tuple = [0, 0, 0];

  /**
   * `upWorld()` allocates a fresh tuple and the forward vector costs a quaternion inversion, while
   * `update` runs every frame: both are bind-static per rig, so they are re-derived only when the
   * bound rig object itself changes. That same identity check is what releases the claim across a
   * re-bind (the old handle would otherwise keep driving a rig that no longer exists).
   */
  let cachedRig: unknown = null;
  let cachedUp: Vec3Tuple = [0, 1, 0];
  let cachedForward: Vec3Tuple = [0, 0, 1];
  const _orientation = new Quaternion();

  function syncRigCache(): void {
    const rig = ctx.session.rig;
    if (rig === cachedRig) return;
    cachedRig = rig;
    cachedUp = rig ? rig.upWorld() : [0, 1, 0];
    cachedForward = rig ? forwardFromWorldCorrection(rig.binding.worldCorrection) : [0, 0, 1];
    // A different rig is a different skeleton: whatever was grabbed is gone.
    active = false;
  }

  function upWorld(): Vec3Tuple {
    syncRigCache();
    return cachedUp;
  }

  function forwardWorld(): Vec3Tuple {
    syncRigCache();
    return cachedForward;
  }

  /** The channel is live: ANIMATE, a bound rig, and a leg length to scale the offset by. */
  function enabled(): boolean {
    const rig = ctx.session.rig;
    if (!rig) return false;
    return ctx.store.getState().mode === 'animate' && rig.legLength() > 0;
  }

  function update(draggingKey: string | null): void {
    // Re-derives the cached vectors and, on a re-bind, drops the claim.
    syncRigCache();
    const on = enabled();
    group.visible = on;
    if (!on) {
      active = false;
      return;
    }

    const rig = ctx.session.rig;
    if (!rig) return;

    const dragging = draggingKey === HIPS_HANDLE_KEY;
    /*
     * The claim follows the SELECTION, not the last pointer event. Bones are selected from the
     * hierarchy panel and the dope sheet too, and those paths never reach `release()` — leaving
     * the height handle outranking the bone gizmo for a bone it has nothing to do with.
     * `pick()` selects `Hips` itself, so this can never cancel a grab the user just made.
     */
    if (!dragging && ctx.store.getState().selection.bone !== 'Hips') active = false;

    material.color.copy(active ? COLOR_HIPS_ACTIVE : COLOR_HIPS);
    dot.scale.setScalar(active ? 1.25 : 1);

    // While the gizmo owns the handle its position IS the gesture; re-gluing would fight the drag.
    if (dragging) return;

    // Off the joint and in front of it — see the header. The offset is a bind-static direction
    // times a bind-static length, so the handle keeps a constant distance as the pelvis moves.
    const position = rig.worldPosition('Hips');
    const reach = HANDLE_OFFSET_LEGS * rig.legLength();
    const forward = forwardWorld();
    handle.position.set(
      position[0] + forward[0] * reach,
      position[1] + forward[1] * reach,
      position[2] + forward[2] * reach,
    );
    handle.quaternion.copy(orientationForUp(upWorld(), _orientation));
    handle.updateMatrixWorld(true);
  }

  const _world = new Vector3();

  function currentWorld(): Vec3Tuple {
    handle.getWorldPosition(_world);
    return [_world.x, _world.y, _world.z];
  }

  function claim(): GizmoClaim | null {
    if (!active || !enabled()) return null;
    return {
      key: HIPS_HANDLE_KEY,
      object: handle,
      mode: 'translate',
      // Local space + a Y-only axis set + a handle oriented by `upWorld()` is what confines the
      // drag to the model's vertical. World space would confine it to the SCENE's vertical, which
      // is only the same thing while the imported root has no rotation of its own.
      space: 'local',
      axes: { x: false, y: true, z: false },
      onChange: () => {
        const rig = ctx.session.rig;
        if (!rig) return;
        const next = hipsYFromDrag(
          startHipsY,
          startWorld,
          currentWorld(),
          upWorld(),
          rig.legLength(),
        );
        ctx.store.actions.setLiveHipsY(next);
      },
      onDragStart: () => {
        startHipsY = ctx.store.getState().liveHipsY;
        const at = currentWorld();
        startWorld[0] = at[0];
        startWorld[1] = at[1];
        startWorld[2] = at[2];
        ctx.store.actions.beginHipsDrag();
      },
      onDragEnd: () => ctx.store.actions.endHipsDrag(),
    };
  }

  const raycaster = new Raycaster();

  function pick(ndc: Vector2, camera: Camera): boolean {
    if (!enabled() || !group.visible) return false;
    raycaster.setFromCamera(ndc, camera);
    if (!raycaster.intersectObject(pickMesh, false)[0]) return false;

    // Before claiming: `syncRigCache` clears the claim on a re-bind, and a pointer event can land
    // between a re-bind and the next frame.
    syncRigCache();
    active = true;
    // Selecting the bone as well is what makes the inspector's "Hips height" field appear in the
    // same gesture — the handle and the field are two views of one value.
    ctx.store.actions.selectBone('Hips');
    return true;
  }

  return {
    update,
    claim,
    pick,
    release(): void {
      active = false;
    },
    probe(): HipsHandleProbe | null {
      if (!enabled()) return null;
      return { worldPosition: currentWorld(), active, visible: group.visible };
    },
    dispose(): void {
      overlayGroup.remove(group);
      dotGeometry.dispose();
      pickGeometry.dispose();
      material.dispose();
      pickMaterial.dispose();
      cachedRig = null;
    },
  };
}
